import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { clientPortalAPI, siteBuilderAPI } from '../lib/api';
import { 
  FiCalendar, FiFileText, FiFile, FiClock, FiCheck, FiX, 
  FiDollarSign, FiEye, FiDownload, FiEdit, FiAlertCircle,
  FiChevronRight, FiChevronLeft, FiUser, FiMail, FiPhone, FiHome,
  FiArrowLeft, FiSettings, FiLogOut, FiImage, FiCamera, FiInfo, FiExternalLink
} from 'react-icons/fi';
import DesignValidation from '../components/client/DesignValidation';
import PhotoGalleryView from '../components/client/PhotoGalleryView';

const ClientPortalPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentReservation, setPaymentReservation] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  // Contract modal state
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractDetail, setContractDetail] = useState(null);
  const [signatureData, setSignatureData] = useState('');
  const [signingContract, setSigningContract] = useState(false);
  const [acceptCGV, setAcceptCGV] = useState(false);
  
  // Reservation detail modal
  const [showReservationDetail, setShowReservationDetail] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  
  // Signature canvas ref
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  
  // Site branding data
  const [site, setSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(true);
  
  // Payment settings (to know if fixed_link mode is active)
  const [paymentSettings, setPaymentSettings] = useState(null);
  
  // User data
  const [user, setUser] = useState(null);
  const [isClient, setIsClient] = useState(false);

  // Calculate deposit amount based on settings
  const getDepositAmount = (reservation) => {
    const totalCost = reservation.total_price || reservation.total_cost || 0;
    
    // If fixed_link mode, use fixed amount
    if (paymentSettings?.deposit_type === 'fixed_link' && paymentSettings?.deposit_fixed_amount) {
      return paymentSettings.deposit_fixed_amount;
    }
    
    // Otherwise use reservation's deposit_amount or calculate percentage
    return reservation.deposit_amount || Math.round(totalCost * (paymentSettings?.deposit_percentage || 30) / 100);
  };

  // Handle deposit payment
  const handlePayDeposit = async (reservation) => {
    setPaymentReservation(reservation);
    setShowPaymentModal(true);
    setPaymentInfo(null);
  };

  const processPayment = async (method) => {
    if (!paymentReservation) return;
    
    setPaymentLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/reservations/${paymentReservation.id}/pay-deposit?payment_method=${method}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentInfo(data);
        
        // If there's a payment link, redirect to it
        if (data.payment_link) {
          window.open(data.payment_link, '_blank');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors du paiement');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Erreur de connexion');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Load contract detail
  const loadContractDetail = async (contractId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/contracts/${contractId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContractDetail(data);
      }
    } catch (error) {
      console.error('Error loading contract:', error);
    }
  };

  // Sign contract
  const handleSignContract = async () => {
    if (!selectedContract || !acceptCGV || !signatureData.trim() || !signatureImage) {
      alert('Veuillez accepter les CGV, taper votre nom complet et signer dans le cadre prévu');
      return;
    }
    
    setSigningContract(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/contracts/${selectedContract.id}/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          signature: signatureData, 
          signature_image: signatureImage,
          accepted_cgv: true 
        })
      });
      
      if (response.ok) {
        alert('Contrat signé avec succès ! Vous pouvez maintenant procéder au paiement de l\'acompte.');
        setShowContractModal(false);
        setSelectedContract(null);
        setSignatureData('');
        setAcceptCGV(false);
        setSignatureImage(null);
        clearSignature();
        // Reload contracts
        loadData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la signature');
      }
    } catch (error) {
      console.error('Sign error:', error);
      alert('Erreur de connexion');
    } finally {
      setSigningContract(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureImage(canvas.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureImage(null);
    }
  };

  // Open reservation detail
  const openReservationDetail = (reservation) => {
    setSelectedReservation(reservation);
    setShowReservationDetail(true);
  };

  // Check authentication and user role
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      // Not logged in, redirect to home
      window.location.href = '/';
      return;
    }
    
    try {
      const userData = JSON.parse(userStr);
      setUser(userData);
      setIsClient(userData.role === 'client');
    } catch (e) {
      window.location.href = '/';
    }
  }, []);

  // Load site data for branding
  useEffect(() => {
    const loadSite = async () => {
      if (!user) return;
      
      try {
        if (isClient && user.parent_user_id) {
          // For clients, load the site of their parent (the business owner)
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sites/by-user/${user.parent_user_id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (res.ok) {
            const data = await res.json();
            setSite(data);
          }
        } else {
          // For business owners, load their own site
          const res = await siteBuilderAPI.getMySite();
          setSite(res.data);
        }
        
        // Load payment settings to know the deposit type
        const paymentRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/payment-settings`);
        if (paymentRes.ok) {
          const paymentData = await paymentRes.json();
          setPaymentSettings(paymentData);
        }
      } catch (error) {
        console.error('Error loading site:', error);
        // Set default branding for Just A Smile Events
        setSite({
          site_name: 'Just A Smile Events',
          general_settings: {
            primaryColor: '#d4af37',
            logoUrl: 'https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png'
          }
        });
      } finally {
        setSiteLoading(false);
      }
    };
    
    if (user) {
      loadSite();
    }
  }, [user, isClient]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [activeTab, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await clientPortalAPI.getDashboard();
        setDashboard(res.data);
      } else if (activeTab === 'reservations') {
        const res = await clientPortalAPI.getReservations();
        setReservations(res.data || []);
      } else if (activeTab === 'invoices') {
        const res = await clientPortalAPI.getInvoices();
        setInvoices(res.data || []);
      } else if (activeTab === 'contracts') {
        const res = await clientPortalAPI.getContracts();
        setContracts(res.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get site branding colors
  const primaryColor = site?.general_settings?.primaryColor || '#d4af37';
  const defaultLogo = 'https://2026.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png';
  const logoUrl = site?.general_settings?.logoUrl || site?.logo_url || site?.site_info?.logo_url || defaultLogo;
  const siteName = site?.site_name || site?.site_info?.name || 'Just A Smile Events';

  const getStatusColor = (status) => {
    const colors = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      approved: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      contract_pending: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
      contract_signed: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
      deposit_pending: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
      deposit_paid: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
      confirmed: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      in_progress: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30' },
      completed: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      payment_pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
      paid: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
      overdue: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
      draft: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
      sent: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      signed: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    };
    return colors[status] || { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' };
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      approved: 'Acceptée',
      contract_pending: 'Contrat à signer',
      contract_signed: 'Contrat signé',
      deposit_pending: 'Acompte à verser',
      deposit_paid: 'Acompte payé',
      confirmed: 'Acompte payé',
      in_progress: 'Prestation en cours',
      completed: 'Terminée',
      payment_pending: 'Solde à régler',
      paid: 'Payée intégralement',
      cancelled: 'Annulée',
      overdue: 'En retard',
      draft: 'Brouillon',
      sent: 'Envoyé',
      signed: 'Signé',
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: FiHome },
    { id: 'reservations', label: 'Mes Réservations', icon: FiCalendar },
    { id: 'invoices', label: 'Mes Factures', icon: FiFileText },
    { id: 'contracts', label: 'Mes Contrats', icon: FiFile },
  ];

  if (siteLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header with site branding */}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and site name */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => window.location.href = '/'}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                title="Retour au site"
              >
                <FiArrowLeft className="h-5 w-5" />
              </button>
              <img 
                src={logoUrl} 
                alt={siteName} 
                className="h-10 object-contain" 
                onError={(e) => { e.target.src = defaultLogo; }}
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-white">{siteName}</h1>
                <p className="text-xs text-white/60">Espace client</p>
              </div>
            </div>
            
            {/* User actions */}
            <div className="flex items-center gap-3">
              {/* Only show settings button for non-clients */}
              {!isClient && (
                <button 
                  onClick={() => navigate('/dashboard/settings')}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <FiSettings className="h-5 w-5" />
                </button>
              )}
              <button 
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 rounded-full font-medium text-sm text-black hover:scale-105 transition-transform"
                style={{ backgroundColor: primaryColor }}
              >
                Voir le site
              </button>
              {/* Logout button for clients */}
              {isClient && (
                <button 
                  onClick={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    // Redirect to the public site
                    window.location.href = '/';
                  }}
                  className="p-2 rounded-full bg-white/10 hover:bg-red-500/20 text-white transition-colors"
                  title="Déconnexion"
                >
                  <FiLogOut className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs with site colors */}
        <div className="bg-white/5 rounded-2xl border border-white/10 mb-8 overflow-hidden">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'text-black border-b-2'
                    : 'text-white/60 hover:text-white border-b-2 border-transparent'
                }`}
                style={{ 
                  backgroundColor: activeTab === tab.id ? primaryColor : 'transparent',
                  borderColor: activeTab === tab.id ? primaryColor : 'transparent'
                }}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && dashboard && (
              <div className="space-y-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Reservations Card */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/5 rounded-2xl border border-white/10 p-6 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <FiCalendar className="h-7 w-7" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Réservations</p>
                        <p className="text-3xl font-bold text-white">{dashboard.reservations?.total || 0}</p>
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-3 gap-2">
                      <div className="bg-yellow-500/10 rounded-xl p-3 text-center">
                        <p className="text-xs text-yellow-400 font-medium">En attente</p>
                        <p className="text-xl font-bold text-yellow-300">{dashboard.reservations?.pending || 0}</p>
                      </div>
                      <div className="bg-green-500/10 rounded-xl p-3 text-center">
                        <p className="text-xs text-green-400 font-medium">Acompte payé</p>
                        <p className="text-xl font-bold text-green-300">{dashboard.reservations?.confirmed || 0}</p>
                      </div>
                      <div className="bg-blue-500/10 rounded-xl p-3 text-center">
                        <p className="text-xs text-blue-400 font-medium">Terminées</p>
                        <p className="text-xl font-bold text-blue-300">{dashboard.reservations?.completed || 0}</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Invoices Card */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white/5 rounded-2xl border border-white/10 p-6 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <FiFileText className="h-7 w-7" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Factures</p>
                        <p className="text-3xl font-bold text-white">{dashboard.invoices?.total || 0}</p>
                      </div>
                    </div>
                    <div className="mt-6 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-white/60">À payer</span>
                        <span className="font-bold text-red-400">{formatCurrency(dashboard.invoices?.total_due)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-white/60">Payées</span>
                        <span className="font-bold text-green-400">{formatCurrency(dashboard.invoices?.total_paid)}</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Contracts Card */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white/5 rounded-2xl border border-white/10 p-6 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <FiFile className="h-7 w-7" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-sm text-white/60">Contrats</p>
                        <p className="text-3xl font-bold text-white">{dashboard.contracts?.total || 0}</p>
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-2 gap-2">
                      <div className="bg-yellow-500/10 rounded-xl p-3 text-center">
                        <p className="text-xs text-yellow-400 font-medium">À signer</p>
                        <p className="text-xl font-bold text-yellow-300">{dashboard.contracts?.pending || 0}</p>
                      </div>
                      <div className="bg-green-500/10 rounded-xl p-3 text-center">
                        <p className="text-xs text-green-400 font-medium">Signés</p>
                        <p className="text-xl font-bold text-green-300">{dashboard.contracts?.signed || 0}</p>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                    <div className="w-2 h-6 rounded-full" style={{ backgroundColor: primaryColor }} />
                    <h2 className="text-lg font-semibold text-white">Activité récente</h2>
                  </div>
                  <div className="divide-y divide-white/5">
                    {(dashboard.recent_reservations || []).length > 0 ? (
                      dashboard.recent_reservations.map((res, i) => (
                        <div key={res.id || i} className="px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                              <FiCalendar className="h-5 w-5" style={{ color: primaryColor }} />
                            </div>
                            <div>
                              <p className="font-medium text-white">{res.product_name || 'Réservation'}</p>
                              <p className="text-sm text-white/60">{formatDate(res.start_date)}</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(res.status).bg} ${getStatusColor(res.status).text}`}>
                            {getStatusLabel(res.status)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="px-6 py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}10` }}>
                          <FiCalendar className="h-8 w-8" style={{ color: primaryColor }} />
                        </div>
                        <p className="text-white/50">Aucune réservation récente</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Reservations Tab */}
            {activeTab === 'reservations' && (
              <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                  <div className="w-2 h-6 rounded-full" style={{ backgroundColor: primaryColor }} />
                  <h2 className="text-lg font-semibold text-white">Mes Réservations</h2>
                </div>
                {reservations.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {reservations.map((res) => {
                      // Calculer l'étape actuelle pour chaque réservation
                      const isApproved = ['approved', 'contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(res.status) || res.contract;
                      const isContractSigned = res.contract?.status === 'signed' || ['contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(res.status);
                      const isDepositPaid = res.deposit_paid || ['deposit_paid', 'confirmed', 'completed', 'paid'].includes(res.status);
                      const isConfirmed = ['confirmed', 'completed', 'paid'].includes(res.status);
                      
                      // Déterminer la prochaine action requise
                      const needsSignature = isApproved && !isContractSigned;
                      const needsPayment = isContractSigned && !isDepositPaid;
                      
                      // Calculer le pourcentage de progression
                      const completedSteps = [true, isApproved, isContractSigned, isDepositPaid, isConfirmed].filter(Boolean).length;
                      const progressPercent = ((completedSteps - 1) / 4) * 100;
                      
                      return (
                        <motion.div 
                          key={res.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 md:p-6 hover:bg-white/5 transition-colors"
                        >
                          {/* Carte de réservation améliorée */}
                          <div className="flex flex-col gap-4">
                            {/* En-tête avec infos principales */}
                            <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => openReservationDetail(res)}>
                              <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}80)` }}>
                                  <FiCalendar className="h-6 w-6 text-black" />
                                </div>
                                <div>
                                  <p className="font-semibold text-white text-lg">{res.product_name}</p>
                                  <p className="text-sm text-white/60">
                                    📅 {formatDate(res.start_date)}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(res.status).bg} ${getStatusColor(res.status).text}`}>
                                      {getStatusLabel(res.status)}
                                    </span>
                                    <span className="text-white font-bold">{formatCurrency(res.total_price || res.total_cost || 0)}</span>
                                  </div>
                                </div>
                              </div>
                              <FiChevronRight className="h-6 w-6 text-white/40 shrink-0 mt-2" />
                            </div>
                            
                            {/* Barre de progression mini */}
                            <div className="w-full">
                              <div className="flex items-center justify-between text-xs text-white/40 mb-1">
                                <span>Progression</span>
                                <span>{completedSteps - 1}/4 étapes</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full transition-all duration-500" 
                                  style={{ width: `${progressPercent}%`, backgroundColor: isConfirmed ? '#22c55e' : primaryColor }}
                                />
                              </div>
                            </div>
                            
                            {/* Zone d'actions - Plus visible et claire */}
                            {(needsSignature || needsPayment || isConfirmed) && (
                              <div className={`rounded-xl p-4 border ${needsSignature || needsPayment ? 'bg-amber-500/10 border-amber-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                                {needsSignature && (
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                                        <FiFileText className="h-5 w-5 text-amber-400" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-white">⚠️ Contrat à signer</p>
                                        <p className="text-xs text-white/60">Signez votre contrat pour continuer</p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (res.contract) {
                                          setSelectedContract(res.contract);
                                          loadContractDetail(res.contract.id);
                                          setShowContractModal(true);
                                        } else {
                                          try {
                                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/contracts`, {
                                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                            });
                                            if (response.ok) {
                                              const contracts = await response.json();
                                              const contract = contracts.find(c => c.reservation_id === res.id);
                                              if (contract) {
                                                setSelectedContract(contract);
                                                loadContractDetail(contract.id);
                                                setShowContractModal(true);
                                              }
                                            }
                                          } catch (error) {
                                            console.error('Error:', error);
                                          }
                                        }
                                      }}
                                      onTouchEnd={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (res.contract) {
                                          setSelectedContract(res.contract);
                                          loadContractDetail(res.contract.id);
                                          setShowContractModal(true);
                                        } else {
                                          try {
                                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/contracts`, {
                                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                            });
                                            if (response.ok) {
                                              const contracts = await response.json();
                                              const contract = contracts.find(c => c.reservation_id === res.id);
                                              if (contract) {
                                                setSelectedContract(contract);
                                                loadContractDetail(contract.id);
                                                setShowContractModal(true);
                                              }
                                            }
                                          } catch (error) {
                                            console.error('Error:', error);
                                          }
                                        }
                                      }}
                                      className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-black active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg touch-manipulation"
                                      style={{ backgroundColor: primaryColor }}
                                    >
                                      <FiFileText className="h-5 w-5" />
                                      Signer le contrat
                                    </button>
                                  </div>
                                )}
                                
                                {needsPayment && (
                                  <div className="flex flex-col gap-4">
                                    {/* Afficher RIB pour les entreprises */}
                                    {(res.client_type === 'entreprise' || res.client_type === 'association') && (
                                      <div className="rounded-xl p-4 border bg-blue-500/10 border-blue-500/30">
                                        <div className="flex items-center gap-3 mb-3">
                                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                            <FiDollarSign className="h-5 w-5 text-blue-400" />
                                          </div>
                                          <div>
                                            <p className="font-medium text-blue-400">🏦 Paiement par virement</p>
                                            <p className="text-xs text-white/60">Compte Crédit Agricole - Entreprises</p>
                                          </div>
                                        </div>
                                        <div className="bg-slate-800/50 rounded-lg p-3 text-sm space-y-1">
                                          <p className="text-white/80"><span className="text-white/50">Titulaire :</span> {site?.ca_bank_account_holder || 'JUST A SMILE EVENTS'}</p>
                                          <p className="text-white/80 font-mono"><span className="text-white/50">IBAN :</span> {site?.ca_bank_iban || 'À configurer'}</p>
                                          <p className="text-white/80 font-mono"><span className="text-white/50">BIC :</span> {site?.ca_bank_bic || 'AGRIFRPP'}</p>
                                          <p className="text-white/80"><span className="text-white/50">Banque :</span> Crédit Agricole</p>
                                        </div>
                                        <p className="text-xs text-amber-400 mt-2">
                                          💡 Référence à indiquer : Réservation {res.id?.slice(0, 8)}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {/* Bouton paiement pour particuliers ou option virement */}
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                      <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                                          <FiDollarSign className="h-5 w-5 text-amber-400" />
                                        </div>
                                        <div>
                                          <p className="font-medium text-white">💳 Acompte à régler</p>
                                          <p className="text-xs text-white/60">
                                            {paymentSettings?.deposit_type === 'fixed_link'
                                              ? `Acompte fixe de ${paymentSettings?.deposit_fixed_amount}€`
                                              : (res.client_type === 'entreprise' || res.client_type === 'association') 
                                                ? 'Effectuez votre virement pour confirmer'
                                                : 'Versez votre acompte pour confirmer'
                                            }
                                          </p>
                                        </div>
                                      </div>
                                      {(res.client_type !== 'entreprise' && res.client_type !== 'association') && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const totalCost = res.total_price || res.total_cost || 0;
                                            const depositAmount = getDepositAmount(res);
                                            handlePayDeposit({ ...res, deposit_amount: depositAmount, total_cost: totalCost });
                                          }}
                                          className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-black hover:scale-105 transition-all flex items-center justify-center gap-2 shadow-lg"
                                          style={{ backgroundColor: primaryColor }}
                                        >
                                          <FiDollarSign className="h-5 w-5" />
                                          Payer {formatCurrency(getDepositAmount(res))}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {isConfirmed && (
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                                      <FiCheck className="h-5 w-5 text-green-400" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-green-400">✅ Acompte payé</p>
                                      <p className="text-xs text-white/60">Rendez-vous le {formatDate(res.start_date)} !</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Message d'attente si en pending */}
                            {res.status === 'pending' && !isApproved && (
                              <div className="rounded-xl p-4 border bg-blue-500/10 border-blue-500/30">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                    <FiClock className="h-5 w-5 text-blue-400" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-blue-400">⏳ En attente de validation</p>
                                    <p className="text-xs text-white/60">Nous reviendrons vers vous rapidement</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-6 py-20 text-center">
                    <div className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}10` }}>
                      <FiCalendar className="h-10 w-10" style={{ color: primaryColor }} />
                    </div>
                    <p className="text-xl font-medium text-white/70 mb-2">Aucune réservation</p>
                    <p className="text-white/40">Vos réservations apparaîtront ici</p>
                  </div>
                )}
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                  <div className="w-2 h-6 rounded-full" style={{ backgroundColor: primaryColor }} />
                  <h2 className="text-lg font-semibold text-white">Mes Factures</h2>
                </div>
                {invoices.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {invoices.map((inv) => (
                      <motion.div 
                        key={inv.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="px-6 py-5 hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${inv.status === 'paid' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                              <FiFileText className={`h-6 w-6 ${inv.status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`} />
                            </div>
                            <div>
                              <p className="font-semibold text-white">{inv.invoice_number}</p>
                              <p className="text-sm text-white/60">{inv.reservation?.product_name || 'Facture'}</p>
                              <p className="text-xs text-white/40">Créée le {formatDate(inv.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-white text-lg">{formatCurrency(inv.total_amount)}</p>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inv.status).bg} ${getStatusColor(inv.status).text}`}>
                                {getStatusLabel(inv.status)}
                              </span>
                            </div>
                            <button className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
                              <FiDownload className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="px-6 py-20 text-center">
                    <div className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}10` }}>
                      <FiFileText className="h-10 w-10" style={{ color: primaryColor }} />
                    </div>
                    <p className="text-xl font-medium text-white/70 mb-2">Aucune facture</p>
                    <p className="text-white/40">Vos factures apparaîtront ici</p>
                  </div>
                )}
              </div>
            )}

            {/* Contracts Tab */}
            {activeTab === 'contracts' && (
              <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                  <div className="w-2 h-6 rounded-full" style={{ backgroundColor: primaryColor }} />
                  <h2 className="text-lg font-semibold text-white">Mes Contrats</h2>
                </div>
                {contracts.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {contracts.map((contract) => (
                      <motion.div 
                        key={contract.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="px-6 py-5 hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${contract.status === 'signed' ? 'bg-green-500/20' : 'bg-violet-500/20'}`}>
                              <FiFile className={`h-6 w-6 ${contract.status === 'signed' ? 'text-green-400' : 'text-violet-400'}`} />
                            </div>
                            <div>
                              <p className="font-semibold text-white">{contract.contract_number}</p>
                              <p className="text-sm text-white/60">{contract.reservation?.product_name || 'Contrat de location'}</p>
                              <p className="text-xs text-white/40">Type : {contract.contract_type === 'rental' ? 'Location' : 'Service'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(contract.status).bg} ${getStatusColor(contract.status).text}`}>
                              {getStatusLabel(contract.status)}
                            </span>
                            {contract.status !== 'signed' && (
                              <button 
                                onClick={() => {
                                  setSelectedContract(contract);
                                  loadContractDetail(contract.id);
                                  setShowContractModal(true);
                                }}
                                className="px-5 py-2.5 rounded-xl font-medium text-sm text-black transition-transform hover:scale-105"
                                style={{ backgroundColor: primaryColor }}
                              >
                                Signer
                              </button>
                            )}
                            <button 
                              onClick={() => {
                                setSelectedContract(contract);
                                loadContractDetail(contract.id);
                                setShowContractModal(true);
                              }}
                              className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                            >
                              <FiEye className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="px-6 py-20 text-center">
                    <div className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}10` }}>
                      <FiFile className="h-10 w-10" style={{ color: primaryColor }} />
                    </div>
                    <p className="text-xl font-medium text-white/70 mb-2">Aucun contrat</p>
                    <p className="text-white/40">Vos contrats apparaîtront ici</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="mt-12 py-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img 
              src={logoUrl} 
              alt={siteName} 
              className="h-8 object-contain" 
              onError={(e) => { e.target.src = defaultLogo; }}
            />
            <span className="text-white/40 text-sm">© {new Date().getFullYear()} {siteName}</span>
          </div>
          <span className="text-white/30 text-xs">Espace Client</span>
        </div>
      </footer>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetail && selectedItem && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowDetail(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gray-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Détail de la réservation</h3>
                <button onClick={() => setShowDetail(false)} className="p-2 hover:bg-white/10 rounded-full text-white transition-colors">
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}80)` }}>
                    <FiCalendar className="h-8 w-8 text-black" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white">{selectedItem.product_name}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedItem.status).bg} ${getStatusColor(selectedItem.status).text}`}>
                      {getStatusLabel(selectedItem.status)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-sm text-white/50 mb-1">Date de début</p>
                    <p className="font-semibold text-white">{formatDate(selectedItem.start_date)}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-sm text-white/50 mb-1">Date de fin</p>
                    <p className="font-semibold text-white">{formatDate(selectedItem.end_date)}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-sm text-white/50 mb-1">Montant total</p>
                    <p className="font-bold text-xl" style={{ color: primaryColor }}>{formatCurrency(selectedItem.total_cost)}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-sm text-white/50 mb-1">Acompte {paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : ''}</p>
                    <p className="font-semibold text-white">{formatCurrency(paymentSettings?.deposit_type === 'fixed_link' ? paymentSettings?.deposit_fixed_amount : selectedItem.deposit_amount)}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Reservation Detail Modal */}
      <AnimatePresence>
        {showReservationDetail && selectedReservation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowReservationDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#1a1a2e] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-white/10"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Détail de la Réservation</h2>
                <button onClick={() => setShowReservationDetail(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <FiX className="h-5 w-5 text-white" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(selectedReservation.status).bg} ${getStatusColor(selectedReservation.status).text}`}>
                    {getStatusLabel(selectedReservation.status)}
                  </span>
                  <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                    {formatCurrency(selectedReservation.total_price || selectedReservation.total_cost)}
                  </span>
                </div>
                
                {/* Product Info */}
                <div className="bg-white/5 rounded-xl p-5 space-y-4">
                  <h3 className="font-semibold text-white text-lg">{selectedReservation.product_name}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-white/50">Date de l'événement</p>
                      <p className="text-white font-medium">{formatDate(selectedReservation.start_date || selectedReservation.event_date)}</p>
                    </div>
                    <div>
                      <p className="text-white/50">Durée</p>
                      <p className="text-white font-medium">{selectedReservation.duration_hours}h</p>
                    </div>
                    <div>
                      <p className="text-white/50">Type d'événement</p>
                      <p className="text-white font-medium">{selectedReservation.event_type || 'Non spécifié'}</p>
                    </div>
                    <div>
                      <p className="text-white/50">Lieu</p>
                      <p className="text-white font-medium">{selectedReservation.event_address || 'Non spécifié'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Options */}
                {selectedReservation.options && selectedReservation.options.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-5">
                    <h4 className="font-semibold text-white mb-3">Options sélectionnées</h4>
                    <div className="space-y-2">
                      {selectedReservation.options.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2 text-white/80">
                          <FiCheck className="h-4 w-4" style={{ color: primaryColor }} />
                          <span>{typeof opt === 'string' ? opt : opt.name || opt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pricing Details */}
                <div className="bg-white/5 rounded-xl p-5 space-y-3">
                  <h4 className="font-semibold text-white mb-3">Détail du prix</h4>
                  <div className="flex justify-between text-white/70">
                    <span>Prestation de base</span>
                    <span>{formatCurrency((selectedReservation.total_price || selectedReservation.total_cost) - (selectedReservation.delivery_fee || 0))}</span>
                  </div>
                  {selectedReservation.delivery_fee > 0 && (
                    <div className="flex justify-between text-white/70">
                      <span>Frais de livraison</span>
                      <span>{formatCurrency(selectedReservation.delivery_fee)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-3 flex justify-between font-bold">
                    <span className="text-white">Total</span>
                    <span style={{ color: primaryColor }}>{formatCurrency(selectedReservation.total_price || selectedReservation.total_cost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">
                      Acompte {paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : `(${paymentSettings?.deposit_percentage || 30}%)`}
                    </span>
                    <span className="text-white">{formatCurrency(getDepositAmount(selectedReservation))}</span>
                  </div>
                </div>
                
                {/* Contract & Payment Status - IMPROVED VERSION */}
                <div className="bg-white/5 rounded-xl p-5 space-y-4">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <FiCalendar className="h-4 w-4" style={{ color: primaryColor }} />
                    Étapes de validation
                  </h4>
                  
                  {/* Progress Bar - Enhanced */}
                  {(() => {
                    const isApproved = ['approved', 'contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status) || selectedReservation.contract;
                    const isContractSigned = selectedReservation.contract?.status === 'signed' || ['contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                    const isDepositPaid = selectedReservation.deposit_paid || ['deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                    const isConfirmed = ['confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                    
                    const completedSteps = [true, isApproved, isContractSigned, isDepositPaid, isConfirmed].filter(Boolean).length;
                    const progress = ((completedSteps - 1) / 4) * 100;
                    
                    return (
                      <div className="relative">
                        {/* Progress indicator dots */}
                        <div className="flex items-center justify-between mb-2">
                          {[1, 2, 3, 4, 5].map((step, i) => {
                            const stepCompleted = completedSteps > i;
                            const stepActive = completedSteps === i + 1;
                            return (
                              <div key={step} className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                                  stepCompleted ? 'bg-green-500 text-white' : 
                                  stepActive ? 'text-black' : 'bg-white/10 text-white/40'
                                }`} style={stepActive ? { backgroundColor: primaryColor } : {}}>
                                  {stepCompleted ? <FiCheck className="h-4 w-4" /> : step}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Progress bar */}
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-700" 
                            style={{ width: `${progress}%`, backgroundColor: isConfirmed ? '#22c55e' : primaryColor }}
                          />
                        </div>
                        <p className="text-sm text-white/60 mt-3 text-center font-medium">
                          {isConfirmed ? '🎉 Acompte payé !' : `Étape ${completedSteps}/5`}
                        </p>
                      </div>
                    );
                  })()}
                  
                  {/* Current Action Card - BIG and VISIBLE */}
                  {(() => {
                    const isApproved = ['approved', 'contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status) || selectedReservation.contract;
                    const isContractSigned = selectedReservation.contract?.status === 'signed' || ['contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                    const isDepositPaid = selectedReservation.deposit_paid || ['deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                    const isConfirmed = ['confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                    
                    const needsSignature = isApproved && !isContractSigned;
                    const needsPayment = isContractSigned && !isDepositPaid;
                    const isPending = selectedReservation.status === 'pending' && !isApproved;
                    
                    if (isPending) {
                      return (
                        <div className="mt-4 p-5 rounded-2xl bg-gradient-to-r from-blue-500/20 to-blue-600/10 border border-blue-500/30">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-blue-500/30 flex items-center justify-center">
                              <FiClock className="h-7 w-7 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-white">⏳ En attente de validation</p>
                              <p className="text-sm text-white/60">Notre équipe examine votre demande</p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    if (needsSignature) {
                      return (
                        <div className="mt-4 p-5 rounded-2xl bg-gradient-to-r from-amber-500/20 to-orange-500/10 border border-amber-500/30">
                          <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl bg-amber-500/30 flex items-center justify-center">
                                <FiFileText className="h-7 w-7 text-amber-400" />
                              </div>
                              <div>
                                <p className="text-lg font-bold text-white">📝 Contrat à signer</p>
                                <p className="text-sm text-white/60">Signez votre contrat pour débloquer le paiement</p>
                              </div>
                            </div>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                if (selectedReservation.contract) {
                                  setSelectedContract(selectedReservation.contract);
                                  loadContractDetail(selectedReservation.contract.id);
                                  setShowContractModal(true);
                                  setShowReservationDetail(false);
                                } else {
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/contracts`, {
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    if (res.ok) {
                                      const contracts = await res.json();
                                      const contract = contracts.find(c => c.reservation_id === selectedReservation.id);
                                      if (contract) {
                                        setSelectedContract(contract);
                                        loadContractDetail(contract.id);
                                        setShowContractModal(true);
                                        setShowReservationDetail(false);
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Error:', error);
                                  }
                                }
                              }}
                              onTouchEnd={async (e) => {
                                e.preventDefault();
                                if (selectedReservation.contract) {
                                  setSelectedContract(selectedReservation.contract);
                                  loadContractDetail(selectedReservation.contract.id);
                                  setShowContractModal(true);
                                  setShowReservationDetail(false);
                                } else {
                                  try {
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/contracts`, {
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    if (res.ok) {
                                      const contracts = await res.json();
                                      const contract = contracts.find(c => c.reservation_id === selectedReservation.id);
                                      if (contract) {
                                        setSelectedContract(contract);
                                        loadContractDetail(contract.id);
                                        setShowContractModal(true);
                                        setShowReservationDetail(false);
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Error:', error);
                                  }
                                }
                              }}
                              className="w-full py-4 rounded-xl font-bold text-lg text-black active:scale-95 transition-all flex items-center justify-center gap-3 shadow-lg touch-manipulation"
                              style={{ backgroundColor: primaryColor }}
                            >
                              <FiFileText className="h-6 w-6" />
                              Signer mon contrat
                            </button>
                          </div>
                        </div>
                      );
                    }
                    
                    if (needsPayment) {
                      const depositAmount = paymentSettings?.deposit_type === 'fixed_link' 
                        ? paymentSettings?.deposit_fixed_amount 
                        : (selectedReservation.deposit_amount || Math.round((selectedReservation.total_price || selectedReservation.total_cost) * (paymentSettings?.deposit_percentage || 30) / 100));
                      return (
                        <div className="mt-4 p-5 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-green-500/10 border border-emerald-500/30">
                          <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl bg-emerald-500/30 flex items-center justify-center">
                                <FiDollarSign className="h-7 w-7 text-emerald-400" />
                              </div>
                              <div>
                                <p className="text-lg font-bold text-white">💳 Acompte à régler</p>
                                <p className="text-sm text-white/60">Versez {formatCurrency(depositAmount)} pour confirmer votre réservation</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const totalCost = selectedReservation.total_price || selectedReservation.total_cost || 0;
                                handlePayDeposit({ ...selectedReservation, deposit_amount: depositAmount, total_cost: totalCost });
                                setShowReservationDetail(false);
                              }}
                              className="w-full py-4 rounded-xl font-bold text-lg text-black hover:scale-[1.02] transition-all flex items-center justify-center gap-3 shadow-lg"
                              style={{ backgroundColor: primaryColor }}
                            >
                              <FiDollarSign className="h-6 w-6" />
                              Payer {formatCurrency(depositAmount)}
                            </button>
                          </div>
                        </div>
                      );
                    }
                    
                    if (isConfirmed) {
                      return (
                        <div className="mt-4 p-5 rounded-2xl bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-500/30">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-green-500/30 flex items-center justify-center">
                              <FiCheck className="h-7 w-7 text-green-400" />
                            </div>
                            <div>
                              <p className="text-lg font-bold text-green-400">✅ Acompte payé</p>
                              <p className="text-sm text-white/60">Rendez-vous le {formatDate(selectedReservation.start_date)} !</p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return null;
                  })()}
                  
                  {/* Steps Timeline - Simplified */}
                  <div className="mt-6 space-y-2">
                    <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Historique</p>
                    {(() => {
                      const isApproved = ['approved', 'contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status) || selectedReservation.contract;
                      const isContractSigned = selectedReservation.contract?.status === 'signed' || ['contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                      const isDepositPaid = selectedReservation.deposit_paid || ['deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                      const isConfirmed = ['confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                      
                      const steps = [
                        { label: 'Demande envoyée', done: true },
                        { label: 'Validation admin', done: isApproved },
                        { label: 'Contrat signé', done: isContractSigned },
                        { label: 'Acompte versé', done: isDepositPaid },
                        { label: 'Acompte payé', done: isConfirmed },
                      ];
                      
                      return steps.map((step, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${step.done ? 'bg-green-500/20' : 'bg-white/5'}`}>
                            {step.done ? <FiCheck className="h-3 w-3 text-green-400" /> : <span className="w-1.5 h-1.5 rounded-full bg-white/20" />}
                          </div>
                          <span className={step.done ? 'text-white/80' : 'text-white/30'}>{step.label}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                
                {/* Design Validation Section - Show after contract is signed */}
                {(() => {
                  const isContractSigned = selectedReservation.contract?.status === 'signed' || 
                    ['contract_signed', 'deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                  const isDepositPaid = selectedReservation.deposit_paid || 
                    ['deposit_paid', 'confirmed', 'completed', 'paid'].includes(selectedReservation.status);
                  
                  // Show design validation after contract signed and deposit paid
                  if (isContractSigned && isDepositPaid) {
                    return (
                      <div className="bg-white/5 rounded-xl p-5 space-y-4">
                        <h4 className="font-semibold text-white flex items-center gap-2">
                          <FiImage className="h-4 w-4" style={{ color: primaryColor }} />
                          Design personnalisé
                        </h4>
                        <div className="bg-slate-900/50 rounded-xl overflow-hidden">
                          <DesignValidation 
                            reservationId={selectedReservation.id}
                            onStatusChange={(newStatus) => {
                              // Refresh the reservation data
                              loadData();
                            }}
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* Photo Gallery Section - Show after event is completed */}
                {(() => {
                  const eventDate = new Date(selectedReservation.start_date);
                  const isEventPast = eventDate < new Date();
                  
                  if (isEventPast) {
                    return (
                      <div className="bg-white/5 rounded-xl p-5 space-y-4">
                        <h4 className="font-semibold text-white flex items-center gap-2">
                          <FiCamera className="h-4 w-4" style={{ color: primaryColor }} />
                          Photos de l'événement
                        </h4>
                        <div className="bg-slate-900/50 rounded-xl overflow-hidden">
                          <PhotoGalleryView reservationId={selectedReservation.id} />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* Documents section - Receipt & Invoice */}
                {['completed', 'paid'].includes(selectedReservation.status) && (
                  <div className="bg-white/5 rounded-xl p-5 space-y-4">
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      <FiFile className="h-4 w-4" style={{ color: primaryColor }} />
                      Documents
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Receipt */}
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center gap-3 mb-2">
                          <FiFileText className="h-5 w-5 text-blue-400" />
                          <span className="text-white font-medium">Reçu</span>
                        </div>
                        {selectedReservation.receipt_url ? (
                          <a 
                            href={selectedReservation.receipt_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors flex items-center gap-2 justify-center"
                          >
                            <FiDownload className="h-4 w-4" />
                            Télécharger
                          </a>
                        ) : (
                          <span className="text-sm text-white/40">En attente de génération</span>
                        )}
                      </div>
                      
                      {/* Invoice */}
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center gap-3 mb-2">
                          <FiFile className="h-5 w-5" style={{ color: primaryColor }} />
                          <span className="text-white font-medium">Facture</span>
                        </div>
                        {selectedReservation.invoice_url ? (
                          <a 
                            href={selectedReservation.invoice_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm px-3 py-2 rounded-lg text-black hover:scale-105 transition-all flex items-center gap-2 justify-center"
                            style={{ backgroundColor: primaryColor }}
                          >
                            <FiDownload className="h-4 w-4" />
                            Télécharger
                          </a>
                        ) : selectedReservation.invoice_requested ? (
                          <span className="text-sm text-yellow-400">Demande en cours...</span>
                        ) : (
                          <button 
                            onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/reservations/${selectedReservation.id}/request-invoice`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                });
                                if (response.ok) {
                                  alert('Demande de facture envoyée ! Vous serez notifié dès qu\'elle sera disponible.');
                                  loadData();
                                }
                              } catch (error) {
                                console.error('Error requesting invoice:', error);
                              }
                            }}
                            className="text-sm px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition-colors w-full"
                          >
                            Demander une facture
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Contract Modal */}
      <AnimatePresence>
        {showContractModal && selectedContract && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-2 md:p-4 overflow-y-auto"
            onClick={() => setShowContractModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#1a1a2e] rounded-2xl w-full max-w-3xl my-4 md:my-0 overflow-hidden border border-white/10 flex flex-col max-h-[95vh] md:max-h-[90vh]"
            >
              {/* Header */}
              <div className="px-4 md:px-6 py-3 md:py-4 border-b border-white/10 flex items-center justify-between shrink-0" style={{ backgroundColor: `${primaryColor}10` }}>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-white">Contrat de Location</h2>
                  <p className="text-white/60 text-xs md:text-sm">N° {selectedContract.contract_number}</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Download button */}
                  <button 
                    onClick={() => {
                      // Generate printable version
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>Contrat ${selectedContract.contract_number}</title>
                          <style>
                            body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px 20px; }
                            .header { text-align: center; margin-bottom: 30px; }
                            .header img { height: 80px; margin-bottom: 20px; }
                            .title { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
                            .subtitle { color: #666; }
                            .section { margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
                            .section-title { font-weight: bold; color: #d4af37; margin-bottom: 10px; }
                            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                            .item { margin: 5px 0; }
                            .label { color: #666; font-size: 12px; }
                            .value { font-weight: 500; }
                            .signature-area { margin-top: 40px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
                            .cgv { font-size: 11px; color: #666; margin-top: 20px; padding: 10px; background: #f5f5f5; border-radius: 4px; }
                            @media print { body { padding: 20px; } }
                          </style>
                        </head>
                        <body>
                          <div class="header">
                            <img src="https://customer-assets.emergentagent.com/job_e9b2c036-3caa-4216-8701-cf2d7a87f3a9/artifacts/19v6u4cc_JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png" alt="Just A Smile Events" style="background:#1a1a2e; padding:10px; border-radius:10px;">
                            <div class="title">CONTRAT DE LOCATION</div>
                            <div class="subtitle">Animation PhotoBooth / VideoBooth - N° ${selectedContract.contract_number}</div>
                          </div>
                          
                          <div class="grid">
                            <div class="section">
                              <div class="section-title">LE LOUEUR</div>
                              <div class="value">Just A Smile Events</div>
                              <div class="item">${contractDetail?.company_info?.address || ''}</div>
                              <div class="item">${contractDetail?.company_info?.phone || ''}</div>
                              <div class="item">${contractDetail?.company_info?.email || ''}</div>
                            </div>
                            <div class="section">
                              <div class="section-title">LE LOCATAIRE</div>
                              <div class="value">${selectedContract.client_name}</div>
                              <div class="item">${selectedContract.client_email}</div>
                              <div class="item">${selectedContract.client_phone}</div>
                              <div class="item">${selectedContract.client_address || ''}</div>
                            </div>
                          </div>
                          
                          <div class="section">
                            <div class="section-title">OBJET DE LA LOCATION</div>
                            <div class="grid" style="grid-template-columns: 1fr 1fr 1fr 1fr;">
                              <div class="item"><div class="label">Prestation</div><div class="value">${selectedContract.product_name}</div></div>
                              <div class="item"><div class="label">Durée</div><div class="value">${selectedContract.duration_hours}h</div></div>
                              <div class="item"><div class="label">Date</div><div class="value">${selectedContract.event_date}</div></div>
                              <div class="item"><div class="label">Type</div><div class="value">${selectedContract.event_type}</div></div>
                            </div>
                            <div class="item" style="margin-top:10px;"><div class="label">Lieu</div><div class="value">${selectedContract.event_address}</div></div>
                            ${selectedContract.options?.length ? `<div class="item" style="margin-top:10px;"><div class="label">Options</div><div class="value">${selectedContract.options.join(', ')}</div></div>` : ''}
                          </div>
                          
                          <div class="section">
                            <div class="section-title">CONDITIONS FINANCIÈRES</div>
                            <div class="item"><div class="label">Montant total TTC</div><div class="value" style="font-size:18px;">${formatCurrency(selectedContract.total_amount)}</div></div>
                            <div class="item"><div class="label">Acompte à la signature ${paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : `(${paymentSettings?.deposit_percentage || 30}%)`}</div><div class="value">${formatCurrency(paymentSettings?.deposit_type === 'fixed_link' ? paymentSettings?.deposit_fixed_amount : selectedContract.deposit_amount)}</div></div>
                            <div class="item"><div class="label">Solde à régler le jour de l'événement</div><div class="value">${formatCurrency(selectedContract.total_amount - (paymentSettings?.deposit_type === 'fixed_link' ? paymentSettings?.deposit_fixed_amount : selectedContract.deposit_amount))}</div></div>
                          </div>
                          
                          <div class="cgv">
                            <strong>CONDITIONS GÉNÉRALES</strong><br>
                            1. Le locataire s'engage à utiliser le matériel loué conformément à sa destination et à le restituer en bon état.<br>
                            2. L'acompte versé à la signature n'est pas remboursable en cas d'annulation moins de 30 jours avant l'événement.<br>
                            3. Le loueur se réserve le droit d'utiliser les photos et vidéos réalisées à des fins promotionnelles, sauf opposition expresse du locataire.<br>
                            4. En cas de dommage au matériel, le locataire sera tenu responsable des réparations ou du remplacement.<br>
                            5. Tout litige relatif au présent contrat sera soumis aux tribunaux compétents du ressort du siège social du loueur.
                          </div>
                          
                          ${selectedContract.status === 'signed' ? `
                          <div class="signature-area">
                            <div class="grid">
                              <div>
                                <div class="label">Signé par</div>
                                <div class="value">${selectedContract.client_signature || selectedContract.client_name}</div>
                              </div>
                              <div>
                                <div class="label">Date de signature</div>
                                <div class="value">${selectedContract.signed_date || ''}</div>
                              </div>
                            </div>
                          </div>
                          ` : ''}
                        </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors"
                    title="Télécharger / Imprimer"
                  >
                    <FiDownload className="h-5 w-5 text-white" />
                  </button>
                  <button onClick={() => setShowContractModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                    <FiX className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>
              
              {/* Contract Content - Scrollable - Enhanced for better reading */}
              <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6 bg-white text-black min-h-[40vh] md:min-h-[45vh]">
                {/* Header with Logo */}
                <div className="text-center border-b pb-4 md:pb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e9b2c036-3caa-4216-8701-cf2d7a87f3a9/artifacts/19v6u4cc_JUST-A-SMILE-EVENTS-LOGO-new-2025-sans-fond-1-1024x1024.png" 
                    alt="Just A Smile Events" 
                    className="h-16 md:h-20 mx-auto mb-4 bg-[#1a1a2e] p-2 rounded-lg"
                  />
                  <h1 className="text-xl md:text-2xl font-bold mb-2">CONTRAT DE LOCATION</h1>
                  <p className="text-gray-600 text-sm">Animation PhotoBooth / VideoBooth</p>
                </div>
                
                {/* Parties */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="border rounded-lg p-3 md:p-4">
                    <h3 className="font-bold mb-2 text-sm md:text-base" style={{ color: primaryColor }}>LE LOUEUR</h3>
                    <p className="font-semibold text-sm md:text-base">Just A Smile Events</p>
                    <p className="text-xs md:text-sm text-gray-600">{contractDetail?.company_info?.address || ''}</p>
                    <p className="text-xs md:text-sm text-gray-600">{contractDetail?.company_info?.phone || ''}</p>
                    <p className="text-xs md:text-sm text-gray-600">{contractDetail?.company_info?.email || ''}</p>
                  </div>
                  <div className="border rounded-lg p-3 md:p-4">
                    <h3 className="font-bold mb-2 text-sm md:text-base" style={{ color: primaryColor }}>LE LOCATAIRE</h3>
                    <p className="font-semibold text-sm md:text-base">{selectedContract.client_name}</p>
                    <p className="text-xs md:text-sm text-gray-600">{selectedContract.client_email}</p>
                    <p className="text-xs md:text-sm text-gray-600">{selectedContract.client_phone}</p>
                    <p className="text-xs md:text-sm text-gray-600">{selectedContract.client_address}</p>
                  </div>
                </div>
                
                {/* Object */}
                <div className="border rounded-lg p-3 md:p-4">
                  <h3 className="font-bold mb-3 text-sm md:text-base" style={{ color: primaryColor }}>OBJET DE LA LOCATION</h3>
                  <div className="grid grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
                    <div>
                      <span className="text-gray-500">Prestation :</span>
                      <span className="ml-2 font-medium">{selectedContract.product_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Durée :</span>
                      <span className="ml-2 font-medium">{selectedContract.duration_hours}h</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Date :</span>
                      <span className="ml-2 font-medium">{formatDate(selectedContract.event_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Type :</span>
                      <span className="ml-2 font-medium">{selectedContract.event_type}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Lieu :</span>
                      <span className="ml-2 font-medium">{selectedContract.event_address}</span>
                    </div>
                  </div>
                  {selectedContract.options && selectedContract.options.length > 0 && (
                    <div className="mt-3 pt-3 border-t text-xs md:text-sm">
                      <span className="text-gray-500">Options :</span>
                      <span className="ml-2 font-medium">{selectedContract.options.join(', ')}</span>
                    </div>
                  )}
                </div>
                
                {/* Pricing */}
                <div className="border rounded-lg p-3 md:p-4">
                  <h3 className="font-bold mb-3 text-sm md:text-base" style={{ color: primaryColor }}>CONDITIONS FINANCIÈRES</h3>
                  <div className="space-y-2 text-xs md:text-sm">
                    <div className="flex justify-between">
                      <span>Montant total TTC</span>
                      <span className="font-bold">{formatCurrency(selectedContract.total_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Acompte à la signature {paymentSettings?.deposit_type === 'fixed_link' ? '(fixe)' : `(${paymentSettings?.deposit_percentage || 30}%)`}</span>
                      <span>{formatCurrency(paymentSettings?.deposit_type === 'fixed_link' ? paymentSettings?.deposit_fixed_amount : selectedContract.deposit_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Solde à régler le jour de l'événement</span>
                      <span>{formatCurrency(selectedContract.total_amount - (paymentSettings?.deposit_type === 'fixed_link' ? paymentSettings?.deposit_fixed_amount : selectedContract.deposit_amount))}</span>
                    </div>
                  </div>
                </div>
                
                {/* CGV */}
                <div className="border rounded-lg p-3 md:p-4 text-[10px] md:text-xs text-gray-600 max-h-32 overflow-y-auto">
                  <h3 className="font-bold mb-2 text-xs md:text-sm text-black">CONDITIONS GÉNÉRALES</h3>
                  <p className="mb-1">1. Le locataire s'engage à utiliser le matériel loué conformément à sa destination et à le restituer en bon état.</p>
                  <p className="mb-1">2. L'acompte versé à la signature n'est pas remboursable en cas d'annulation moins de 30 jours avant l'événement.</p>
                  <p className="mb-1">3. Le loueur se réserve le droit d'utiliser les photos et vidéos réalisées à des fins promotionnelles, sauf opposition expresse du locataire.</p>
                  <p className="mb-1">4. En cas de dommage au matériel, le locataire sera tenu responsable des réparations ou du remplacement.</p>
                  <p>5. Tout litige relatif au présent contrat sera soumis aux tribunaux compétents du ressort du siège social du loueur.</p>
                </div>
              </div>
              
              {/* Signature Section - Compact design for better contract reading */}
              {selectedContract.status !== 'signed' ? (
                <div className="p-3 md:p-4 border-t border-white/10 space-y-2 md:space-y-3 shrink-0 bg-[#1a1a2e]">
                  {/* Compact signature form - grid layout on larger screens */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Left column: Name and checkbox */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="acceptCGV"
                          checked={acceptCGV}
                          onChange={(e) => setAcceptCGV(e.target.checked)}
                          className="w-4 h-4 rounded shrink-0"
                        />
                        <label htmlFor="acceptCGV" className="text-white/80 text-xs">
                          J'accepte les conditions générales
                        </label>
                      </div>
                      
                      <div>
                        <label className="block text-white/70 text-xs mb-1">Nom et prénom</label>
                        <input
                          type="text"
                          value={signatureData}
                          onChange={(e) => setSignatureData(e.target.value)}
                          placeholder="Prénom NOM"
                          className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-white/40 text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Right column: Canvas signature */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-white/70 text-xs">Signature manuscrite</label>
                        <button 
                          type="button"
                          onClick={clearSignature}
                          className="text-[10px] text-white/50 hover:text-white/80 px-2 py-0.5 rounded border border-white/20"
                        >
                          Effacer
                        </button>
                      </div>
                      <div className="border border-white/20 rounded-lg overflow-hidden bg-white/5">
                        <canvas
                          ref={canvasRef}
                          width={350}
                          height={80}
                          className="w-full touch-none cursor-crosshair"
                          style={{ background: 'rgba(255,255,255,0.02)' }}
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSignContract}
                    disabled={signingContract || !acceptCGV || !signatureData.trim() || !signatureImage}
                    className="w-full py-3 rounded-xl font-bold text-black transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {signingContract ? 'Signature en cours...' : 'Signer le contrat'}
                  </button>
                </div>
              ) : (
                <div className="p-6 border-t border-white/10 text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full text-green-400">
                    <FiCheck className="h-5 w-5" />
                    Contrat signé le {formatDate(selectedContract.signed_date)}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && paymentReservation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-white/10">
                <h3 className="text-xl font-bold text-white">Payer l'acompte</h3>
                <p className="text-white/60 text-sm mt-1">
                  {paymentReservation.product_name} - {formatDate(paymentReservation.start_date)}
                </p>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Amount - Use paymentInfo.amount if available (for fixed_link), otherwise calculate */}
                <div className="bg-white/5 rounded-2xl p-6 mb-6 text-center">
                  <p className="text-white/60 text-sm mb-2">Montant de l'acompte</p>
                  <p className="text-4xl font-bold" style={{ color: primaryColor }}>
                    {formatCurrency(
                      paymentInfo?.amount || 
                      paymentReservation.deposit_amount || 
                      (paymentReservation.total_cost * 0.3)
                    )}
                  </p>
                  {paymentInfo?.type === 'fixed_link' && (
                    <p className="text-white/50 text-xs mt-2">Acompte fixe</p>
                  )}
                </div>

                {!paymentInfo ? (
                  <>
                    <p className="text-white/70 text-sm mb-4 text-center">Choisissez votre mode de paiement :</p>
                    
                    {/* Payment Methods */}
                    <div className="space-y-3">
                      <button
                        onClick={() => processPayment('card')}
                        disabled={paymentLoading}
                        className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/30 bg-white/5 transition-all disabled:opacity-50"
                      >
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                          <FiDollarSign className="h-6 w-6" style={{ color: primaryColor }} />
                        </div>
                        <div className="text-left flex-1">
                          <p className="font-medium text-white">Carte bancaire</p>
                          <p className="text-sm text-white/50">Paiement sécurisé en ligne</p>
                        </div>
                        {paymentLoading && <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor }} />}
                      </button>

                      <button
                        onClick={() => processPayment('transfer')}
                        disabled={paymentLoading}
                        className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/30 bg-white/5 transition-all disabled:opacity-50"
                      >
                        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                          <FiFileText className="h-6 w-6 text-blue-400" />
                        </div>
                        <div className="text-left flex-1">
                          <p className="font-medium text-white">Virement bancaire</p>
                          <p className="text-sm text-white/50">Recevoir les coordonnées bancaires</p>
                        </div>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center">
                    {paymentInfo.payment_link ? (
                      <>
                        <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                          <FiDollarSign className="h-8 w-8" style={{ color: primaryColor }} />
                        </div>
                        <p className="text-white font-medium mb-2">
                          {paymentInfo.type === 'fixed_link' ? `Acompte de ${paymentInfo.amount}€ à régler` : 'Lien de paiement généré !'}
                        </p>
                        
                        {/* Information message for fixed_link payments */}
                        {paymentInfo.info_message && (
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4 text-left">
                            <p className="text-blue-400 text-sm flex items-start gap-2">
                              <FiInfo className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span>{paymentInfo.info_message}</span>
                            </p>
                          </div>
                        )}
                        
                        {!paymentInfo.info_message && (
                          <p className="text-white/60 text-sm mb-4">Une nouvelle fenêtre devrait s'ouvrir pour procéder au paiement.</p>
                        )}
                        
                        <a 
                          href={paymentInfo.payment_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-black"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <FiExternalLink className="h-4 w-4" />
                          Procéder au paiement
                        </a>
                        
                        <p className="text-white/40 text-xs mt-4">
                          {paymentInfo.type === 'fixed_link' 
                            ? 'Vous serez redirigé vers notre plateforme de paiement sécurisée'
                            : 'Le lien s\'ouvre dans une nouvelle fenêtre'
                          }
                        </p>
                      </>
                    ) : paymentInfo.bank_info ? (
                      <>
                        <div className="w-16 h-16 rounded-full bg-blue-500/20 mx-auto mb-4 flex items-center justify-center">
                          <FiFileText className="h-8 w-8 text-blue-400" />
                        </div>
                        <p className="text-white font-medium mb-4">Coordonnées bancaires</p>
                        {paymentInfo.bank_info.iban && !paymentInfo.bank_info.iban.includes('XXXX') ? (
                          <div className="bg-white/5 rounded-xl p-4 text-left space-y-3">
                            <div>
                              <p className="text-white/50 text-xs">IBAN</p>
                              <p className="text-white font-mono text-sm">{paymentInfo.bank_info.iban}</p>
                            </div>
                            <div>
                              <p className="text-white/50 text-xs">BIC</p>
                              <p className="text-white font-mono text-sm">{paymentInfo.bank_info.bic}</p>
                            </div>
                            <div>
                              <p className="text-white/50 text-xs">Bénéficiaire</p>
                              <p className="text-white text-sm">{paymentInfo.bank_info.beneficiary}</p>
                            </div>
                            <div>
                              <p className="text-white/50 text-xs">Référence (obligatoire)</p>
                              <p className="font-bold text-sm" style={{ color: primaryColor }}>{paymentInfo.bank_info.reference}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-orange-500/10 rounded-xl p-4 text-left border border-orange-500/30">
                            <p className="text-orange-400 text-sm mb-2">⚠️ Coordonnées bancaires non configurées</p>
                            <p className="text-white/70 text-sm">Veuillez contacter directement l'établissement pour obtenir les coordonnées de virement.</p>
                            <div className="mt-3 pt-3 border-t border-orange-500/20">
                              <p className="text-white/50 text-xs">Référence à mentionner :</p>
                              <p className="font-bold text-sm" style={{ color: primaryColor }}>{paymentInfo.bank_info.reference}</p>
                            </div>
                          </div>
                        )}
                        <p className="text-white/50 text-xs mt-4">
                          ⚠️ N'oubliez pas d'indiquer la référence dans le motif du virement
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-full bg-orange-500/20 mx-auto mb-4 flex items-center justify-center">
                          <FiAlertCircle className="h-8 w-8 text-orange-400" />
                        </div>
                        <p className="text-white font-medium mb-2">{paymentInfo.message || 'Information'}</p>
                        <p className="text-white/60 text-sm">Veuillez contacter l'établissement pour finaliser le paiement.</p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentInfo(null);
                  }}
                  className="w-full py-3 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientPortalPage;
